// S2 (v0.13.0) — super admin: tenant list.
import { Link, useNavigate } from "react-router-dom";
import {
  PlusIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  EyeIcon,
} from "lucide-react";

import {
  useAdminTenants,
  useReactivateTenant,
  useSuspendTenant,
} from "@/api/adminTenants";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useFormatTimestamp } from "@/utils/datetime";

export default function TenantListPage() {
  const { data: tenants = [], isLoading, isError, refetch } = useAdminTenants();
  const suspend = useSuspendTenant();
  const reactivate = useReactivateTenant();
  const fmt = useFormatTimestamp();
  const nav = useNavigate();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tenants</h1>
          <p className="text-sm text-muted-foreground">
            Platform-level tenant directory · super admin only
          </p>
        </div>
        <Button
          onClick={() => nav("/admin/tenants/create")}
          className="bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90"
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          Create Tenant
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Slug</th>
                <th className="px-3 py-2 text-left">Plan</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Cameras</th>
                <th className="px-3 py-2 text-right">Users</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isError ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center">
                    {/* ERROR IS NOT EMPTY. An empty table reads as a fact about
                        the fleet; a failed fetch must say so and offer a retry. */}
                    <span className="text-destructive">Could not load tenants.</span>{" "}
                    <button
                      type="button"
                      onClick={() => refetch()}
                      className="underline underline-offset-2"
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : tenants.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                    No tenants yet.
                  </td>
                </tr>
              ) : (
                tenants.map((t) => (
                  <tr key={t.id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">
                      <Link
                        to={`/admin/tenants/${t.id}`}
                        className="hover:underline"
                      >
                        {t.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {t.slug}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="online">{t.plan}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        variant={
                          t.status === "active"
                            ? "online"
                            : t.status === "suspended"
                              ? "error"
                              : "offline"
                        }
                      >
                        {t.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">{t.camera_count}</td>
                    <td className="px-3 py-2 text-right">{t.user_count}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {fmt(t.created_at, "date")}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          asChild
                        >
                          <Link to={`/admin/tenants/${t.id}`}>
                            <EyeIcon className="h-3 w-3" />
                          </Link>
                        </Button>
                        {t.status === "active" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              // A9/AU-200 — confirm before suspending a whole tenant.
                              if (
                                window.confirm(
                                  `Suspend tenant "${t.name}"? Every user in ` +
                                    `this tenant will lose access until reactivated.`,
                                )
                              )
                                suspend.mutate(t.id);
                            }}
                            title="Suspend"
                          >
                            <PauseCircleIcon className="h-3 w-3" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => reactivate.mutate(t.id)}
                            title="Reactivate"
                          >
                            <PlayCircleIcon className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
